/**
 * API client for the Snoop dashboards.
 *
 * Mirrors the function surface of the standalone dashboard's api/client.js so
 * the ported components/pages work unchanged, but targets this backend's
 * /api/snoop/* routes and sends the JWT the rest of the app uses.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Shared JSON fetch. Pass `signal` (AbortController) to cancel in-flight requests. */
export async function fetchJson(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // This backend returns {error}; the reference API returned {detail}.
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Snoop search bounds — keep in sync with SNOOP_SEARCH_* in
 * backend/app/services/snoop.py. Shorter queries are ignored server-side
 * (a single letter matches ~200k rows).
 */
export const SNOOP_SEARCH_MIN_CHARS = 2;
export const SNOOP_SEARCH_LIMIT = 500;

export function fetchSnoopSummary({ minNewFollows, allFollowsAnd, q } = {}) {
  const params = new URLSearchParams();
  if (minNewFollows != null) params.set('min_new_follows', String(minNewFollows));
  if (q) params.set('q', q);
  if (Array.isArray(allFollowsAnd)) {
    for (const name of allFollowsAnd) {
      const s = String(name || '').trim();
      if (s) params.append('all_follows_and', s);
    }
  }
  const query = params.toString();
  return fetchJson(`/api/snoop/summary${query ? `?${query}` : ''}`);
}

/** Snoop sources that followed an IG user (Snoop summary row expand). */
export function fetchSnoopCoreFollowers(igUserId, limit = 500, { coreOnly = true } = {}) {
  const q = new URLSearchParams({
    ig_user_id: String(igUserId || '').replace(/\.0$/, ''),
    limit: String(limit),
    core_only: coreOnly ? 'true' : 'false',
  });
  return fetchJson(`/api/snoop/core-followers?${q}`);
}

export function fetchSnoopMapping(limit) {
  const q = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  return fetchJson(`/api/snoop/mapping${q}`);
}

/** Create/upsert a snoop_mapping row from the Add Mapping modal. */
export function createSnoopMapping({ igUsername, status, igUserId, igFullName } = {}) {
  return fetchJson('/api/snoop/mapping', {
    method: 'POST',
    body: JSON.stringify({
      ig_username: igUsername,
      status,
      ig_user_id: igUserId || undefined,
      ig_full_name: igFullName || undefined,
    }),
  });
}

/** Remove snoop_mapping row(s) by IG User ID (preferred) or username. */
export function deleteSnoopMapping({ igUserId, igUsername } = {}) {
  return fetchJson('/api/snoop/mapping', {
    method: 'DELETE',
    body: JSON.stringify({
      ig_user_id: igUserId || undefined,
      ig_username: igUsername || undefined,
    }),
  });
}

/** Update snoop_mapping.status (Snoop Status column) — DB only. */
export function updateSnoopMappingStatus({
  igUserId,
  status,
  igUsername,
  igFullName,
  snapshotDate,
} = {}) {
  return fetchJson('/api/snoop/mapping/status', {
    method: 'PATCH',
    body: JSON.stringify({
      ig_user_id: igUserId,
      status,
      ig_username: igUsername || undefined,
      ig_full_name: igFullName || undefined,
      snapshot_date: snapshotDate || undefined,
    }),
  });
}

/** Update artist_mappings.status (Snoop Artist Status column) — DB only. */
export function updateArtistMappingStatus({
  uid,
  status,
  instagram,
  artist,
} = {}) {
  return fetchJson('/api/snoop/artist-status', {
    method: 'PATCH',
    body: JSON.stringify({
      uid,
      status,
      instagram: instagram || undefined,
      artist: artist || undefined,
    }),
  });
}
